// src/components/EventSeries.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackPageView } from "../utils/tracking";
import { recordThemeSelection, captureFirstBlogPostViewed } from "../utils/sourceTracking";
import Footer from "./Footer";

/**
 * Public directory of published landing pages ("event series"), for
 * visitors who arrived organically (e.g. a Google search on the homepage)
 * rather than via a specific landing page link, so they can pick which
 * city/event they actually want before continuing to sign up.
 */
const EventSeries = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredSlug, setHoveredSlug] = useState(null);
  const isLoggedIn = !!localStorage.getItem("memberstackToken");

  useEffect(() => {
    trackPageView('Event Series Directory');

    // Redundant capture point for the blog CTA's ?blog_ref= param, in case
    // the originating blog post page's own JS write (captureFirstBlogPostViewed
    // in BlogPost.js) somehow didn't fire - belt and braces, since this is
    // the very next hop in the blog -> /events -> theme -> signup journey.
    const blogRef = new URLSearchParams(window.location.search).get('blog_ref');
    if (blogRef) {
      captureFirstBlogPostViewed(blogRef);
    }

    const fetchEntries = async () => {
      try {
        const response = await fetch('/api/event-series');
        if (!response.ok) {
          throw new Error('Failed to load event series');
        }
        const data = await response.json();
        setEntries(data);
      } catch (err) {
        console.error('Error fetching event series:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, []);

  const handleSelect = (slug) => {
    recordThemeSelection(slug);
    navigate(`/theme/${slug}`);
  };

  // meetup_urlname is a raw URL slug (e.g. "Birmingham-Tech-Network") - not
  // meant to be shown to visitors as-is, so turn it into a readable name.
  const formatMeetupName = (urlname) => {
    if (!urlname) return null;
    return urlname
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const cardStyle = (slug) => ({
    display: 'block',
    textAlign: 'left',
    width: '100%',
    padding: '24px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(139, 95, 191, 0.12)',
    boxShadow: hoveredSlug === slug
      ? '0 12px 30px rgba(139, 95, 191, 0.18)'
      : '0 4px 15px rgba(139, 95, 191, 0.08)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transform: hoveredSlug === slug ? 'translateY(-4px)' : 'translateY(0)',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  });

  return (
    <div style={{ backgroundColor: '#FAFAFA', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes es-shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .es-shimmer {
          animation: es-shimmer 1.6s infinite;
          background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
          background-size: 400px 100%;
        }
      `}</style>

      {/* Top Navigation Banner - matches Home.js/ThemeLandingPage.js */}
      <section
        style={{
          background: "linear-gradient(135deg, #FBFAFF, #F5F2FF)",
          borderBottom: "1px solid rgba(139, 95, 191, 0.08)",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "18px 0",
          }}>
            <a
              href="/"
              style={{
                fontSize: "2rem",
                fontWeight: "800",
                lineHeight: "44px",
                background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                margin: 0,
                textDecoration: "none",
                display: "inline-block",
              }}
            >
              OkGather
            </a>
            <a
              href={isLoggedIn ? "/edit-profile" : "/login"}
              style={{
                background: "transparent",
                color: "#8B5FBF",
                padding: "12px 24px",
                borderRadius: "25px",
                textDecoration: "none",
                fontSize: "0.95rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                border: "2px solid #8B5FBF",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#8B5FBF";
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#8B5FBF";
              }}
            >
              {isLoggedIn ? "My Profile" : "Login"}
            </a>
          </div>
        </div>
      </section>

      <section style={{
        padding: '50px 20px 30px',
        background: 'linear-gradient(135deg, #FBFAFF, #F5F2FF)',
        borderBottom: '1px solid rgba(139, 95, 191, 0.1)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 4.5vw, 2.5rem)',
            fontWeight: '800',
            marginBottom: '12px',
            background: 'linear-gradient(135deg, #8B5FBF, #6B46C1)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: '1.2',
            paddingBottom: '0.1em',
          }}>
            Find Your Event Series
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#666', margin: 0 }}>
            Choose the gathering that's right for you.
          </p>
        </div>
      </section>

      <section style={{ flex: 1, padding: '40px 20px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        {loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '20px',
          }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{
                padding: '24px',
                backgroundColor: 'white',
                borderRadius: '16px',
                border: '1px solid rgba(139, 95, 191, 0.12)',
                boxShadow: '0 4px 15px rgba(139, 95, 191, 0.08)',
              }}>
                <div className="es-shimmer" style={{ height: '22px', width: '65%', marginBottom: '14px', borderRadius: '6px' }} />
                <div className="es-shimmer" style={{ height: '24px', width: '45%', marginBottom: '10px', borderRadius: '999px' }} />
                <div className="es-shimmer" style={{ height: '14px', width: '35%', borderRadius: '4px' }} />
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#FCEAEA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              margin: '0 auto 20px',
            }}>
              ⚠️
            </div>
            <p style={{ fontSize: '1.05rem', color: '#c0392b', margin: '0 0 20px', fontWeight: '600' }}>
              Something went wrong loading event series. Please try again shortly.
            </p>
            <a
              href="/events"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #8B5FBF, #2c2e96)',
                color: '#fff',
                padding: '12px 28px',
                borderRadius: '25px',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: '600',
                boxShadow: '0 2px 10px rgba(139, 95, 191, 0.2)',
              }}
            >
              Try Again
            </a>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <p style={{ fontSize: '1.05rem', color: '#666', margin: '0 0 20px' }}>
              No event series are available right now.
            </p>
            <a
              href="/create-account"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #8B5FBF, #2c2e96)',
                color: '#fff',
                padding: '14px 36px',
                borderRadius: '25px',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '700',
                boxShadow: '0 4px 15px rgba(139, 95, 191, 0.25)',
              }}
            >
              Join Us
            </a>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '20px',
          }}>
            {entries.map((entry) => (
              <button
                key={entry.url_slug}
                onClick={() => handleSelect(entry.url_slug)}
                onMouseEnter={() => setHoveredSlug(entry.url_slug)}
                onMouseLeave={() => setHoveredSlug(null)}
                style={cardStyle(entry.url_slug)}
              >
                <h3 style={{
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  color: '#333',
                  margin: '0 0 8px',
                  lineHeight: '1.3',
                }}>
                  {formatMeetupName(entry.meetup_urlname) || entry.theme_name}
                </h3>
                {/* City is the primary disambiguator between options for the
                    same theme - shown right below the title as a solid
                    badge. location_id is required on new landing pages (see
                    AdminThemeLandingPages.js), but older entries may still
                    lack it - in that case, just omit this line entirely
                    rather than show a placeholder. */}
                {entry.location_name && (
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    backgroundColor: '#6B46C1',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    letterSpacing: '0.01em',
                    marginBottom: '10px',
                  }}>
                    📍 {entry.location_name}
                  </span>
                )}
                {(entry.meetup_event_title || entry.theme_name) && (
                  <p style={{ fontSize: '1rem', color: '#777', margin: 0 }}>
                    {entry.meetup_event_title || entry.theme_name}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default EventSeries;
